import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPlus, TbTrash } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { toast } from 'react-toastify';
import PageHeader from '../../components/misc/PageHeader';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import Input from '../../components/misc/Input';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import {
  ContributionFrequency,
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
  CreateContributionPlanDto,
} from '../../../types/bankAccount';
import Labeled from '../../components/misc/Labeled';
import CurrencyInput from '../../components/forms/CurrencyInput';
import DatePicker from '../../components/forms/DatePicker';
import ConnectionsList from '../../components/misc/ConnectionsList';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import DropdownSelect from '../../components/forms/DropdownSelect';
import * as bankAccountService from '../../../services/bankAccountService';
import Avatar from '../../components/misc/Avatar';
import ProgressBar from '../../components/misc/ProgressBar';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';

interface ContributionPlansPageProps {
  hideHeader?: boolean;
}

export default function ContributionPlansPage({
  hideHeader = false,
}: ContributionPlansPageProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  const navigate = useNavigate();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [plans, setPlans] = useState<ContributionPlanWithMembersModel[]>([]);
  const [plansWithPayments, setPlansWithPayments] = useState<ContributionPlanWithPayments[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _.chain(_connections.filter((c) => c.type === ConnectionType.B2C_CONNECTION))
    .groupBy((c) => +c.id)
    .mapValues((v) => v[0])
    .value();

  useEffect(() => {
    bankAccountService.getAllContributionPlans().then(setPlans);
  }, []);

  useEffect(() => {
    // Fetch detailed payment information for each plan
    const fetchPlansWithPayments = async () => {
      const plansWithPaymentsData = await Promise.all(
        plans.map((plan) =>
          bankAccountService.getContributionPlanWithPayments(plan.contributionPlanId),
        ),
      );
      setPlansWithPayments(plansWithPaymentsData);
    };

    if (plans.length > 0) {
      fetchPlansWithPayments();
    }
  }, [plans]);

  const createContributionPlan = async (plan: CreateContributionPlanDto): Promise<void> => {
    bankAccountService.createContributionPlan(plan).then((createdPlan) => {
      setPlans((prevPlans) => [
        ...prevPlans,
        {
          ...createdPlan,
          appliesTo: plan.appliesToConnectionIds.map((id) => ({ connectionId: id })),
        },
      ]);
      setOpenCreateModal(false);
    });
  };

  const deleteContributionPlan = async (planId: number) => {
    try {
      await bankAccountService.deleteContributionPlan(planId);
      setPlans((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      setPlansWithPayments((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      toast.success(t('success.deleted'));
    } catch (error: any) {
      toast.error(t('error.deletionFailed', { error: error.message }));
    }
  };

  const handleRowClick = (planId: number) => {
    navigate(`/financial/contribution-plans/${planId}`);
  };

  const calculateProgress = (plan: ContributionPlanWithPayments) => {
    const totalAmount = plan.amount;
    const paidAmount = plan.contributionPayments.reduce(
      (sum, payment) => sum + payment.transaction.amount,
      0,
    );
    return totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
  };

  const getPaidAmount = (plan: ContributionPlanWithPayments) => {
    return plan.contributionPayments.reduce((sum, payment) => sum + payment.transaction.amount, 0);
  };

  // Filter plans based on search query
  const filteredPlans = useMemo(() => {
    if (!searchQuery.trim()) {
      return plans;
    }

    const query = searchQuery.toLowerCase().trim();
    return plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) || plan.description.toLowerCase().includes(query),
    );
  }, [plans, searchQuery]);

  return (
    <>
      {!hideHeader ? (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={() => setOpenCreateModal(true)}>
              <TbPlus /> {t('create')}
            </Button>
          }
        />
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {plans.length > 0 && (
            <>
              <SearchBar
                className="max-w-[380px]"
                search={searchQuery}
                setSearch={setSearchQuery}
              />
              <Button variant="primary-dark" onClick={() => setOpenCreateModal(true)}>
                <TbPlus /> {t('create')}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {plans.length === 0 ? (
          <EmptyScreenView
            title={t('noPlans')}
            subtitle={t('createFirstPlan')}
            primaryButton={
              <Button variant="primary-dark" onClick={() => setOpenCreateModal(true)}>
                <TbPlus /> {t('createFirstButton')}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-8">
            <div className="relative w-full overflow-x-auto">
              <BaseTable>
                <BaseTableHeader>
                  <BaseTableHeaderRow>
                    <HeaderCell>{t('table.name')}</HeaderCell>
                    <HeaderCell>{t('table.amount')}</HeaderCell>
                    <HeaderCell>{t('table.installments')}</HeaderCell>
                    <HeaderCell>{t('table.assignedTo')}</HeaderCell>
                    <HeaderCell>{t('table.paid')}</HeaderCell>
                    <HeaderCell>{t('table.actions')}</HeaderCell>
                  </BaseTableHeaderRow>
                </BaseTableHeader>
                <BaseTableBody>
                  <BaseTableSpacerRow />
                  {filteredPlans.map((plan, index) => {
                    const planWithPayments = plansWithPayments.find(
                      (p) => p.contributionPlanId === plan.contributionPlanId,
                    );
                    const progress = planWithPayments ? calculateProgress(planWithPayments) : 0;
                    const paidAmount = planWithPayments ? getPaidAmount(planWithPayments) : 0;

                    return (
                      <BaseTableDataRow
                        className="group cursor-pointer transition-all hover:bg-secondary-200"
                        key={`${plan.contributionPlanId}_tr_${index}`}>
                        <DataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                          <p className="text-sm font-medium">{plan.name}</p>
                          <p className="text-xs text-gray-400">
                            {plan.description.slice(0, 50)}
                            {plan.description.length >= 50 && '...'}
                          </p>
                        </DataCell>
                        <DataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                          {formatEuroCents(plan.amount, i18n.language)}
                        </DataCell>
                        <DataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                          {plan.numberOfInstallments}
                        </DataCell>
                        <DataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                          <AvatarList
                            connections={plan.appliesTo.map(
                              ({ connectionId }) => connections[connectionId],
                            )}
                          />
                        </DataCell>
                        <DataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                          <div className="flex flex-col gap-1">
                            <ProgressBar
                              progress={progress}
                              variant="primary-light"
                              showText={false}
                            />
                            <div className="flex justify-between text-xs">
                              <span>{progress.toFixed(0)}%</span>
                              <span>{formatEuroCents(paidAmount, i18n.language)}</span>
                            </div>
                          </div>
                        </DataCell>
                        <DataCell>
                          <ActionMenu>
                            <ActionMenuButton
                              icon={TbTrash}
                              text={t('table.delete')}
                              className="text-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteContributionPlan(plan.contributionPlanId);
                              }}
                            />
                          </ActionMenu>
                        </DataCell>
                      </BaseTableDataRow>
                    );
                  })}
                </BaseTableBody>
              </BaseTable>
            </div>
          </div>
        )}
      </div>

      <CreateContributionPlanModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        onSubmit={createContributionPlan}
      />
    </>
  );
}

function AvatarList({ connections }: { connections: Connection[] }) {
  return (
    <div className="flex items-center gap-[-8px]">
      {connections.slice(0, 2).map((c, index) => (
        <Avatar
          key={`avatar_contribution_${index}_${c?.id}`}
          className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
          src={c?.picture}
          alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
        />
      ))}
      {connections.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{connections.length - 2}
        </div>
      )}
    </div>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}

type CreateContributionPlanModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (plan: CreateContributionPlanDto) => Promise<void>;
};

function CreateContributionPlanModal({
  open,
  setOpen,
  onSubmit,
}: CreateContributionPlanModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans.createModal' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _connections.filter((c) => c.type === ConnectionType.B2C_CONNECTION);
  const [contributionPlan, setContributionPlan] = useState<CreateContributionPlanDto>({
    name: '',
    description: '',
    amount: 0,
    frequency: ContributionFrequency.ONCE,
    startDate: new Date(),
    numberOfInstallments: 1,
    appliesToConnectionIds: [],
  });

  const handleChange = (
    name: keyof CreateContributionPlanDto,
    value: string | Date | undefined | ContributionFrequency | number | number[],
  ) => {
    setContributionPlan((prevPlan) => ({
      ...prevPlan,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (contributionPlan.frequency === ContributionFrequency.ONCE) {
      handleChange('numberOfInstallments', 1);
    }
  }, [contributionPlan.frequency]);

  const handleSubmit = () => {
    onSubmit(contributionPlan).then(() => {
      setContributionPlan({
        name: '',
        description: '',
        amount: 0,
        frequency: ContributionFrequency.ONCE,
        startDate: new Date(),
        numberOfInstallments: 1,
        appliesToConnectionIds: [],
      });
    });
  };

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl">
      <div className="flex max-h-[calc(100vh-5rem)] flex-col gap-4 overflow-y-auto rounded-md bg-secondary-50 p-6">
        <h2 className="text-lg font-medium">{t('title')}</h2>

        <div className="flex flex-col gap-2">
          <Input
            id="plan-name"
            type="text"
            label={t('name')}
            placeholder={t('namePlaceholder')}
            onChange={(e) => handleChange('name', e.target.value)}
            value={contributionPlan.name}
          />
          <Labeled label={t('description')} htmlFor="plan-description">
            <textarea
              id="plan-description"
              rows={2}
              className="h-[unset]"
              placeholder={t('descriptionPlaceholder')}
              onChange={(e) => handleChange('description', e.target.value)}
              value={contributionPlan.description}
            />
          </Labeled>
          <CurrencyInput
            id="plan-amount"
            type="text"
            label={t('amount')}
            placeholder={t('amountPlaceholder')}
            handleChange={(value) => handleChange('amount', Math.floor(value * 100))}
          />
          <Labeled label={t('frequency')}>
            <DropdownSelect
              options={Object.values(ContributionFrequency).map((freq) => ({
                value: freq,
                label: tEnum(`ContributionFrequency.${freq}`),
              }))}
              id="plan-amount"
              value={contributionPlan.frequency}
              setValue={(value) => handleChange('frequency', value)}
              placeholder={t('amountPlaceholder')}
            />
          </Labeled>
          <div className="flex items-center gap-2">
            <Labeled label={t('startDate')}>
              <DatePicker
                date={contributionPlan.startDate}
                setDate={(date) => handleChange('startDate', date)}
                includeTime={false}
              />
            </Labeled>
            <Labeled label={t('numberOfInstallments')}>
              <Input
                // TODO validate that this is a positive integer and bind to value
                value={contributionPlan.numberOfInstallments.toFixed(0)}
                type="number"
                className="disabled:opacity-50"
                min={1}
                disabled={contributionPlan.frequency === ContributionFrequency.ONCE}
                onChange={(e) => handleChange('numberOfInstallments', parseInt(e.target.value, 10))}
                placeholder={t('numberOfInstallmentsPlaceholder')}
              />
            </Labeled>
          </div>
          <p className="font-medium">{t('appliesToExplanation')}</p>
          <ConnectionsList
            connections={connections.map((c) => ({
              id: c.id,
              alias: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
              picture: c.picture,
            }))}
            selected={contributionPlan.appliesToConnectionIds}
            setSelected={(ids) => handleChange('appliesToConnectionIds', ids)}
          />
        </div>

        <Button className="self-end" onClick={handleSubmit} variant="primary-dark">
          {t('createButton')}
        </Button>
      </div>
    </ModalLayout>
  );
}
